import { useRef, useState } from 'react';

import { ChartTypeRegistry } from 'chart.js';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetReportingColumnsQuery } from 'apis/report-configuration.api';
import { useDistributeMutation, useGetTemplateByIdQuery, usePostExecuteQueryMutation } from 'apis/report-templates.api';
import {
  chartTypeMap,
  getDisplayTypeFromDataType,
  getTypeFromDataType,
  normalize,
  parseQualifiedColumn,
  TableRow,
  typeStringMap,
  VisualizationType,
  visualizationTypeByIdMap,
} from 'constants/reports-templates';
import { ROUTES } from 'constants/routes';
import { useOpen } from 'hooks/useOpen';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import {
  ChartDataset,
  ConfigAggregate,
  ConfigFilter,
  ConfigJoin,
  ConfigSortItem,
  ConfigVisualization,
  DisplayColumn,
  JoinRow,
  NormalizedFilter,
  PreviewConfig,
  SortingRule,
  TableColumnConfig,
  VisualizationItemPayload,
} from 'interfaces/report-templates.interface';
import { CommonError } from 'interfaces/shared.interface';
import { ErrorModal, LoadingOverlay } from 'shared-components';
import { getRandomColor } from 'utils/global';

import CreateReportTemplateFiltersBlock, { ReportTemplateFiltersBlockHandle } from './CreateReportTemplateFiltersBlock';
import CreateReportTemplateHeader from './CreateReportTemplateHeader';
import CreateReportTemplateSchedule from './CreateReportTemplateSchedule';

const CreateReportTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { data: selectedColumnsData } = useGetReportingColumnsQuery();

  const [postExecuteQuery, { isLoading: isExecuteQueryLoading, error: executeError }] = usePostExecuteQueryMutation();

  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { data: templateData, isLoading: isTemplateLoading } = useGetTemplateByIdQuery(id ?? '', {
    skip: !isEditMode,
  });

  const [postDistribute, { isLoading: isDistributeLoading, error: distributeError }] = useDistributeMutation();
  const filtersBlockRef = useRef<ReportTemplateFiltersBlockHandle>(null);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(false);
  const [chartData, setChartData] = useState<{ type: keyof ChartTypeRegistry; data: ChartDataset | null } | null>(null);
  const [visualizationType, setVisualizationType] = useState<string | undefined>();
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>();
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [tableColumns, setTableColumns] = useState<TableColumnConfig[]>([]);
  const errorData = (executeError as CommonError) || (distributeError as CommonError);
  const errorDetails = errorData?.data?.error as { message?: string; details?: string };

  const [isOpenErrorModal, handleOpenErrorModal, handleCloseErrorModal] = useOpen();

  const handleReset = () => {
    filtersBlockRef.current?.resetForm();
    setChartData(null);
    setVisualizationType(undefined);
  };

  const mapConfigJoins = (joins?: ConfigJoin[]): JoinRow[] => {
    if (!Array.isArray(joins) || joins.length === 0) {
      return [] as JoinRow[];
    }

    const toMeta = (col: ReportingColumn) => ({
      name: col.name,
      tableName: col.tableName,
      type: col.columnType,
      displayName: col.name,
      displayType: typeStringMap[col.columnType],
    });

    return joins
      .map((join) => {
        const leftCol = selectedColumnsData?.result.find(
          (c: ReportingColumn) => c.name === join.leftColumn && c.tableName === join.leftTable
        );
        const rightCol = selectedColumnsData?.result.find(
          (c: ReportingColumn) => c.name === join.rightColumn && c.tableName === join.rightTable
        );
        if (!leftCol || !rightCol) {
          return null;
        }

        return {
          id: join.id,
          leftTable: join.leftTable,
          rightTable: join.rightTable,
          leftColumn: join.leftColumn,
          rightColumn: join.rightColumn,
          column: toMeta(leftCol),
          joinColumn: toMeta(rightCol),
        };
      })
      .filter(<T,>(x: T | null): x is T => x != null);
  };

  const normalizeConfigFilters = (filters?: ConfigFilter[], table?: string): NormalizedFilter[] => {
    if (!Array.isArray(filters) || filters.length === 0) {
      return [] as NormalizedFilter[];
    }

    return filters
      .map((filter) => {
        let filterCol: string | null = filter.column;
        let filterTable = table;
        if (typeof filterCol === 'string') {
          const { table: tbl, column } = parseQualifiedColumn(filterCol);
          if (tbl) {
            filterTable = tbl;
            filterCol = column;
          }
        }

        const colMeta = selectedColumnsData?.result.find(
          (c: ReportingColumn) => c.name === filterCol && c.tableName === filterTable
        );
        if (!colMeta) {
          return null;
        }

        const dataType = typeStringMap[colMeta.columnType];
        if (!dataType) {
          return null;
        }

        return {
          column: {
            name: colMeta.name,
            displayName: colMeta.name,
            type: getTypeFromDataType(dataType),
            displayType: getDisplayTypeFromDataType(dataType),
            tableName: colMeta.tableName,
          },
          condition: filter.condition,
          value: filter.value,
          isLocked: filter.locked,
        };
      })
      .filter(<T,>(x: T | null): x is T => x != null);
  };

  const normalizeConfigSorting = (sorting?: ConfigSortItem[], table?: string): SortingRule[] => {
    if (!Array.isArray(sorting) || sorting.length === 0) {
      return [] as SortingRule[];
    }

    return sorting
      .map((sortItem, index) => {
        let sortCol: string = sortItem.column;
        let sortTable = table;
        if (typeof sortCol === 'string') {
          const { table: tbl, column } = parseQualifiedColumn(sortCol);
          if (tbl) {
            sortTable = tbl;
            sortCol = column;
          }
        }

        const colMeta = selectedColumnsData?.result.find(
          (c: ReportingColumn) => c.name === sortCol && c.tableName === sortTable
        );
        if (!colMeta) {
          return null;
        }

        const dataType = typeStringMap[colMeta.columnType];
        if (!dataType) {
          return null;
        }

        return {
          column: {
            name: colMeta.name,
            displayName: colMeta.name,
            type: getTypeFromDataType(dataType),
            displayType: getDisplayTypeFromDataType(dataType),
            tableName: colMeta.tableName,
          },
          direction: sortItem.direction === 'asc' ? 1 : 2,
          order: index + 1,
        };
      })
      .filter(<T,>(x: T | null): x is T => x != null);
  };

  const buildBaseColumn = (
    visualization: ConfigVisualization | undefined,
    typedConfig: PreviewConfig,
    tableName: string
  ): VisualizationItemPayload | null => {
    if (!visualization) {
      return null;
    }

    if (visualization.column === 'Count') {
      return { name: 'Count', displayName: 'Count', type: 3, displayType: 'INT', tableName };
    }

    const aggregate = (typedConfig.aggregates ?? []).find((agg: ConfigAggregate) => {
      const normalizedFunc = agg.func.endsWith(' of') ? agg.func.slice(0, -3) : agg.func;
      const fullName = `${normalizedFunc} of ${agg.column}`;
      return fullName.trim() === visualization.column?.trim();
    });

    if (aggregate) {
      return {
        name: aggregate.column,
        displayName: `${aggregate.func} of ${aggregate.column}`,
        type: 3,
        displayType: 'INT',
        tableName,
      };
    }

    const columnMeta = selectedColumnsData?.result.find(
      (col: ReportingColumn) => col.name === visualization.column && col.tableName === tableName
    );
    if (!columnMeta) {
      return null;
    }

    const rawType = columnMeta.columnType;
    if (rawType == null) {
      return null;
    }

    const dataType = typeStringMap[rawType];
    if (!dataType) {
      return null;
    }

    return {
      name: columnMeta.name,
      displayName: columnMeta.name,
      type: getTypeFromDataType(dataType),
      displayType: getDisplayTypeFromDataType(dataType),
      tableName,
    };
  };

  const handleDiscard = () => {
    navigate(ROUTES.reportTemplates);
  };

  const handlePreviewClick = async () => {
    if (!filtersBlockRef.current) {
      return;
    }

    const config = filtersBlockRef.current.getCurrentConfiguration();
    if (!config) {
      return;
    }

    try {
      const typedConfig = config as unknown as PreviewConfig;
      const { visualization, tableName, joins, filters } = typedConfig;

      const mappedJoins = mapConfigJoins(joins);
      const normalizedFilters = normalizeConfigFilters(filters, tableName);
      const normalizedSorting = normalizeConfigSorting(typedConfig.sorting, tableName);

      setVisualizationType(visualization?.type);
      setSelectedColumn(visualization?.column);

      if (visualization?.type !== 'Table') {
        const baseColumn = buildBaseColumn(visualization, typedConfig, tableName);
        if (!baseColumn) {
          return;
        }

        const payload = {
          query: {
            tableName,
            joins: mappedJoins,
            sorting: normalizedSorting,
            filters: normalizedFilters,
            displayColumns: [baseColumn],
            ...(Array.isArray(typedConfig.groupBy) && typedConfig.groupBy.length === 1
              ? { groupBy: typedConfig.groupBy[0] }
              : {}),
          },
          column: baseColumn,
          timelapseType: visualization.timelapseType ?? 2,
        };

        const response = await postDistribute(payload).unwrap();

        const preparedChartData = response?.result
          ? {
              labels: response.result.labels,
              datasets: [
                {
                  label: `${tableName}/${visualization.column}`,
                  data: response.result.values,
                  backgroundColor: response.result.values.map(() => getRandomColor(0.5)),
                  borderColor: 'rgba(0,0,0,0.1)',
                  borderWidth: 1,
                },
              ],
            }
          : null;

        setChartData({ type: chartTypeMap[visualization.type], data: preparedChartData });
      } else {
        const displayColumns = (visualization.columns ?? [])
          .map((colName: string) => {
            const { table, column } = parseQualifiedColumn(String(colName));
            const tbl = table ?? tableName;
            const colMeta = selectedColumnsData?.result.find(
              (col: ReportingColumn) => col.name === column && col.tableName === tbl
            );

            if (!colMeta) {
              return null;
            }

            const rawType = colMeta.columnType;
            const dataType = typeStringMap[rawType];
            if (!dataType) {
              return null;
            }

            return {
              name: colMeta.name,
              displayName: `${colMeta.tableName} → ${colMeta.name}`,
              type: getTypeFromDataType(dataType),
              displayType: getDisplayTypeFromDataType(dataType),
              tableName: colMeta.tableName,
            };
          })
          .filter(<T,>(x: T | null): x is T => x != null);

        const response = await postExecuteQuery({
          query: {
            tableName,
            joins: mappedJoins,
            sorting: normalizedSorting,
            filters: normalizedFilters,
            displayColumns,
            rowLimit: visualization.rowLimit,
          },
        }).unwrap();

        const cols = displayColumns.map((col) => ({
          field: `${col.tableName} -> ${col.name}`,
          header: col.displayName || `${col.tableName} -> ${col.name}`,
          name: col.name,
          tableName: col.tableName,
        }));

        setTableColumns(cols);
        setTableData([...response.result]);
      }
    } catch (error) {
      handleOpenErrorModal();
    }
  };

  const initialVisualization = (() => {
    const result = templateData?.result;
    const typeId = result?.visualizationTypeId;
    const columnName = result?.distributionColumn?.name;

    if (!typeId) {
      return null;
    }

    if (typeId === 1) {
      const displayCols = result?.query?.displayColumns ?? [];
      const colIds = displayCols.map((dc: DisplayColumn) => normalize(dc.id ?? dc.name));
      return {
        type: 'Table' as const,
        columns: colIds,
        rowLimit: result?.query?.rowLimit,
      };
    }

    if (!columnName) {
      return null;
    }

    const displayColumns = result?.query?.displayColumns ?? [];
    const aggDisplay = displayColumns.find(
      (col: DisplayColumn) => col.aggregateFunctionType && col.name === columnName
    );
    const preferredColumn = aggDisplay?.displayName ?? columnName;

    return {
      type: visualizationTypeByIdMap[typeId] as Exclude<VisualizationType, 'Table'>,
      column: preferredColumn,
    };
  })();

  const columnsProp: (TableColumnConfig | string)[] = (() => {
    if (visualizationType === 'Table') {
      return tableColumns;
    }

    if (selectedColumn) {
      return [selectedColumn];
    }

    return [];
  })();

  return (
    <div data-cy="report-templates-create-page" className="flex flex-col h-screen mx-[-40px] my-[-24px]">
      <ErrorModal
        errorMessage={errorDetails?.message}
        description={errorDetails?.details}
        isOpen={isOpenErrorModal}
        handleClose={handleCloseErrorModal}
      />

      <CreateReportTemplateHeader
        onReset={handleReset}
        onDiscard={handleDiscard}
        isPreviewEnabled={isPreviewEnabled}
        handlePreviewClick={handlePreviewClick}
        filtersBlockRef={filtersBlockRef}
        isEditMode={isEditMode}
        templateId={id}
      />

      <div className="flex h-full">
        <div className="flex-1 overflow-hidden w-full">
          <LoadingOverlay isLoading={isDistributeLoading || isExecuteQueryLoading} />
          <CreateReportTemplateSchedule
            chartJsType={chartData?.type ?? 'bar'}
            chartJsData={chartData?.data ?? { labels: [], datasets: [] }}
            visualizationType={visualizationType}
            columns={columnsProp}
            tableData={tableData}
          />
        </div>

        {!isTemplateLoading && (
          <CreateReportTemplateFiltersBlock
            key={id}
            initialValues={templateData?.result || null}
            selectedColumnsData={selectedColumnsData?.result}
            ref={filtersBlockRef}
            onValidationChange={setIsPreviewEnabled}
            initialVisualization={initialVisualization}
          />
        )}
      </div>
    </div>
  );
};

export default CreateReportTemplatePage;
